<script>
    import R, {LOADER} from "../lib/requests";
    import {flip} from 'svelte/animate';
    import {createEventDispatcher} from 'svelte/internal';
    import Alert from "../stores/alerts";
    import Notifier from "../stores/notifier";
    import AppIcon from "../components/icons/AppIcon.svelte";
    import AccuracyControl from "../components/controls/AccuracyControl.svelte";
    import AnalysesGroupSelectControl from "../components/controls/AnalysesGroupSelectControl.svelte";
    import Checkbox from "../components/controls/Checkbox.svelte";
    import AnalysesSelectControl from "../components/controls/AnalysesSelectControl.svelte";
    import CheckboxIcon from "../components/icons/CheckboxIcon.svelte";

    export let analysesGroups = [];
    export let analyses = [];
    export let isLocked = 0;
    export let selectedId = 0;

    let checkAllCodes = false;
    let checkAllSubcontractors = false;
    let hovering = false;

    const dispatch = createEventDispatcher();

    const onChange = () => {
        dispatch("change");
    }

    const onValueKeyup = (event) => {
        if (event.key === 'Enter') {
            moveToNextValueInput(event.target);
        }
    }

    const moveToNextValueInput = (currentInput) => {
        let currentIndex = ~~currentInput.getAttribute('data-index');
        let nextInput = null;

        const table = currentInput.closest('table');
        const inputs = Array.prototype.slice.call(
            table.querySelectorAll('.analysis-value')
        );

        inputs.map(item => {
            let index = ~~item.getAttribute('data-index');
            if (!nextInput && index > currentIndex) {
                nextInput = item;
            }
        });

        if (!nextInput && inputs.length) {
            nextInput = inputs[0];
        }

        if (nextInput) {
            nextInput.focus();
        }
    }

    const emptyAnalysis = (analysis_id = 0, code = '', name_el = '') => {
        return {
            analysis_id,
            code,
            name_el,
            name_en: '',
            value: '',
            accuracy: '',
        }
    }

    const onGroupSelect = ({detail}) => {

        const onSuccess = result => {

            let newAdded = 0;
            result.map(item => {
                let analysis = analyses.find(x => x.analysis_id == item.id);
                if (!analysis) {
                    analyses = [...analyses, emptyAnalysis(item.id, item.code, item.name_el)];
                    newAdded++;
                }
            });
            if (newAdded > 0) {
                dispatch("change");
            }
        }

        const onFailure = err => {
        };

        let data = {
            group_id: detail.id,
            sort_field: 'view_index',
        }
        R.handle(R.analyses.index, data, onSuccess, onFailure, LOADER.POPUP);
    }

    const addAnalyses = ({detail}) => {
        if (analyses.filter(x => x.analysis_id == detail.id).length) {
            Notifier.showError("Η ανάλυση υπάρχει ήδη στο δείγμα!");
            return;
        }

        analyses = [...analyses, emptyAnalysis(detail.id, detail.code, detail.name_el)];
        dispatch("change");
    }

    const onDelete = () => {
        let count = analyses.filter(x => x.is_checked == 1).length;
        if (count == 0) {
            Alert.warning("Διαγραφή αναλύσεων", "Δεν έχετε επιλέξει αναλύσεις για διαγραφή.");
            return;
        }

        Alert.delete("Διαγραφή αναλύσεων", `Πρόκειται να αφαιρέσετε ${count} αναλύσεις από το δείγμα.\nΘέλετε να συνεχίσετε;`, () => deleteAnalyses());
    }

    const deleteAnalyses = () => {
        analyses = analyses.filter(x => x.is_checked != 1);
        dispatch("change");
    }

    const onRowClick = (item) => {
        selectedId = item.analysis_id;
    }

    const onToggleAnalysesCodes = () => {
        checkAllCodes = !checkAllCodes;
        analyses.map(item => {
            item.is_checked = checkAllCodes ? 1 : 0;
        });
        analyses = analyses;
    }

    const onToggleSubcontractors = () => {
        checkAllSubcontractors = !checkAllSubcontractors;
        analyses.map(item => {
            item.is_subcontractor = checkAllSubcontractors ? 1 : 0;
        });
        analyses = analyses;
    }

    const onToggleBold = item => {
        item.is_bold = item.is_bold == 1 ? 0 : 1;
        analyses = analyses;
        dispatch("change");
    }

    /* Drag drop handlers */
    const dragstart = (event, i) => {
        selectedId = analyses[i].analysis_id;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.dropEffect = 'move';
        event.dataTransfer.setData('text/plain', i);
    }

    const drop = (event, target) => {
        event.dataTransfer.dropEffect = 'move';
        const start = parseInt(event.dataTransfer.getData("text/plain"));
        const newTracklist = analyses;

        if (start < target) {
            newTracklist.splice(target + 1, 0, newTracklist[start]);
            newTracklist.splice(start, 1);
        } else {
            newTracklist.splice(target, 0, newTracklist[start]);
            newTracklist.splice(start + 1, 1);
        }
        analyses = newTracklist;
        hovering = null;
        dispatch("change");
    }
</script>

<div class="panel-heading fw-semibold py-1">
    Αναλύσεις
    {#if !isLocked}
        <div class="d-flex align-items-center">
            <div class="add-analyses">
                <AnalysesSelectControl right small on:select={ addAnalyses }/>
            </div>
            <AnalysesGroupSelectControl groups={analysesGroups} on:select={ onGroupSelect }/>
            <button class="btn btn-default ms-1" title="διαγραφή επιλεγμένων αναλύσεων" on:click={ onDelete }>
                <AppIcon icon="trash" strokeWidth="2"/>
            </button>
        </div>
    {/if}
</div>
{#if analyses.length}
    <table class="table table-list table-sm table-analyses">
        <thead class="hidden-sm hidden-xs">
        <tr>
            {#if isLocked}
                <th>ΚΩΔΙΚΟΣ</th>
            {:else}
                <th class="d-flex align-items-center pointer" on:click={ onToggleAnalysesCodes }>
                    <CheckboxIcon size="extra-small" cssClass="me-1" checked="{ checkAllCodes }"/>
                    ΚΩΔ.
                </th>
            {/if}
            <th class="hidden-md hidden-sm hidden-xs">ΠΑΡΑΜΕΤΡΟΣ</th>
            <th>ΑΠΟΤΕΛΕΣΜΑ</th>
            <th>ΑΚΡΙΒΕΙΑ</th>
            {#if isLocked}
                <th>
                    <div class="hidden-md">ΥΠΕΡΓΟΛΑΒΟΣ</div>
                    <div class="hidden-xl hidden-lg">ΥΠ.</div>
                </th>
            {:else}
                <th class="d-flex align-items-center pointer" on:click={ onToggleSubcontractors }>
                    <CheckboxIcon size="extra-small" cssClass="me-1" checked="{ checkAllSubcontractors }"/>
                    ΥΠΕΡΓ.
                </th>
            {/if}
        </tr>
        </thead>
        <tbody>
        {#each analyses as analysis, index (analysis.analysis_id)}
            <tr class:selected={ selectedId == analysis.analysis_id } on:click={ () => onRowClick(analysis) }
                animate:flip="{{ duration: 100 }}"
                draggable={true}
                on:dragstart={event => dragstart(event, index)}
                on:drop|preventDefault={event => drop(event, index)}
                ondragover="return false"
                on:dragenter={() => hovering = index}
                class:drag-over={hovering === index}
            >
                <td class="code">
                    {#if !isLocked}
                        <Checkbox label="{ analysis.code }" bind:checked={ analysis.is_checked }/>
                    {:else}
                        { analysis.code }
                    {/if}
                </td>
                <td class="parameter text-gray hidden-md hidden-sm hidden-xs">{ analysis.name_el }</td>
                <td class="value">
                    {#if isLocked}
                        <div class:fw-bold={analysis.is_bold == 1}>
                            { analysis.value }
                        </div>
                    {:else}
                        <div class="value-inner">
                            <button class="btn px-2 py-1"
                                    class:btn-default={analysis.is_bold == 0}
                                    class:btn-primary={analysis.is_bold == 1}
                                    on:click={() => onToggleBold(analysis)}>
                                B
                            </button>
                            <input type="text" class="form-control form-control-sm minimal text-end analysis-value"
                                   class:fw-bold={analysis.is_bold == 1}
                                   bind:value="{ analysis.value }" on:focus={ () => onRowClick(analysis) }
                                   on:change={ onChange }
                                   on:keyup={ onValueKeyup }
                                   data-index="{ index }"
                            >
                        </div>
                    {/if}
                </td>
                <td class="accuracy">
                    {#if isLocked}
                        { analysis.accuracy }
                    {:else}
                        <AccuracyControl id="accuracy{ analysis.code }" css="form-control-sm minimal"
                                         bind:value="{ analysis.accuracy }"
                                         on:focus={ () => onRowClick(analysis) }
                                         on:change={ onChange }
                        />
                    {/if}
                </td>
                <td class="subcontractor text-center">
                    <Checkbox inline bind:checked={analysis.is_subcontractor} on:change={ onChange }
                              disabled="{ isLocked == 1}"/>
                </td>
            </tr>
        {/each}
        </tbody>
    </table>
{:else}
    <div class="panel-body panel-pad">
        <div class="text-gray-1">Το δείγμα δεν περιέχει αναλύσεις</div>
    </div>
{/if}

<style>
    .table-analyses {
        background: #fbfbfb;
    }

    .table-analyses tr.selected {
        background: #f3f3f3;
    }

    .table-analyses .code {
        width: 40px;
    }

    /*.table-analyses .parameter {*/

    /*}*/

    .table-analyses .value {
        width: 180px;
        text-align: right;
    }

    .table-analyses .value .value-inner {
        width: 100%;
        display: flex;
    }

    .table-analyses .accuracy {
        width: 180px;
    }

    .table-analyses .subcontractor {
        width: 110px;
    }


    @media (max-width: 576px) {
        .table-analyses {

        }
    }

    @media (min-width: 768px) and (max-width: 991px) {
        .table-analyses .subcontractor {
            width: 40px;
        }
    }

    .add-analyses {
        width: 100px;
    }

    /*.sample-analyses {*/

    /*}*/

    /*.sample-analyses .result {*/
    /*    display: flex;*/
    /*    align-items: center;*/
    /*}*/

    /*.sample-analyses .result .code {*/

    /*}*/

    /*.sample-analyses .result .parameter {*/
    /*    max-width: 200px;*/
    /*}*/

    /*.sample-analyses .result .value {*/

    /*}*/

    /*.sample-analyses .result .accuracy {*/

    /*}*/
</style>